import { useContext, useState } from 'react';

import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'i18n';
import {
  CUBOH_MENU_CHILD_MAP,
  MENU_OBJECT_TYPES,
  TYPE_TO_READABLE_STRING_MAP,
} from 'constants/menuManagement';
import { useMenuManagement } from 'hooks/useMenuManagement';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import { canEditMasterMenuItems, getPosSyncDisabledTooltip } from 'helpers/masterMenuPermissions';
import type {
  ParentRecord,
  UseAddNewDrawersResult,
} from 'components/MenuManagement/MenuItemActions/types/newDrawerTypes';

const NS = 'menuManagementAddNew';

// Add-flow drawer state, shared by the toolbar button and the row actions popover
export const useAddNewDrawers = ({
  parentRecord,
}: {
  parentRecord: Partial<ParentRecord>;
}): UseAddNewDrawersResult => {
  // useSuspense: false - this runs on every tree row, and the page's Suspense has no fallback
  const { t } = useTranslation(NS, { useSuspense: false });
  const { menu } = useContext(MenuEdit.Context);
  const [addTypeChoiceDrawerOpen, addTypeChoiceDrawerHandlers] = useDisclosure(false);
  const [addNewDrawerOpen, addNewDrawerHandlers] = useDisclosure(false);
  const [addItemFromPosDrawerOpen, addItemFromPosDrawerHandlers] = useDisclosure(false);
  const [fromTypeChoice, setFromTypeChoice] = useState(false);

  const { getMerchantIntegrationsFromState } = useMenuManagement();

  const merchantIntegrations = getMerchantIntegrationsFromState(parentRecord?.merchantId || '');

  const enforceShooterPosLimitations =
    merchantIntegrations?.enforcePosConditions && !merchantIntegrations?.useV2Mapping;

  const disabledByMasterMenuAccess =
    !!menu?.masterMenuPermissionTier && !canEditMasterMenuItems(menu.masterMenuPermissionTier);
  const disabledByPos = !!enforceShooterPosLimitations && !parentRecord?.hasReadOnlyMenu;
  const disabledByPosSync = !!merchantIntegrations?.isPosSyncInPlace;

  const newItemType: string =
    CUBOH_MENU_CHILD_MAP[parentRecord?.itemType as keyof typeof CUBOH_MENU_CHILD_MAP];
  const newItemReadableType = (
    TYPE_TO_READABLE_STRING_MAP[newItemType as keyof typeof TYPE_TO_READABLE_STRING_MAP] || ''
  ).toLowerCase();
  const isAddingCategory = newItemType === MENU_OBJECT_TYPES.categories;

  const handleAddClick = () => {
    if (enforceShooterPosLimitations && isAddingCategory) {
      addTypeChoiceDrawerHandlers.open();
    } else if (enforceShooterPosLimitations) {
      addItemFromPosDrawerHandlers.open();
    } else {
      setFromTypeChoice(false);
      addNewDrawerHandlers.open();
    }
  };

  let disabledReason;
  if (disabledByMasterMenuAccess) {
    disabledReason = t('useAddNewDrawers.masterMenuDisabled', { newItemReadableType });
  } else if (disabledByPosSync) {
    disabledReason = getPosSyncDisabledTooltip();
  } else if (disabledByPos) {
    disabledReason = t('useAddNewDrawers.posDisabled', { newItemReadableType });
  }

  return {
    handleAddClick,
    isDisabled: !!disabledReason,
    disabledReason,
    newItemReadableType,
    drawerProps: {
      parentRecord,
      itemType: newItemType,
      addNewDrawerOpen,
      addNewDrawerHandlers,
      addTypeChoiceDrawerOpen,
      addTypeChoiceDrawerHandlers,
      addItemFromPosDrawerOpen,
      addItemFromPosDrawerHandlers,
      fromTypeChoice,
      setFromTypeChoice,
    },
  };
};
